import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Info, Lightbulb, ChevronDown, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * GuidedInput — the reusable Nirvana form field.
 * Props: label, helperText, exampleText, acceptedFormat, validationError,
 *        whyItMatters, required, textarea, dataTestId, ...inputProps
 */
export function GuidedInput({
  label,
  helperText,
  exampleText,
  acceptedFormat,
  validationError,
  whyItMatters,
  required = false,
  textarea = false,
  dataTestId,
  className,
  ...inputProps
}) {
  const [showWhy, setShowWhy] = useState(false);
  const Field = textarea ? Textarea : Input;

  return (
    <div className={cn("space-y-1.5", className)}>
      <div className="flex items-center justify-between gap-3">
        <Label className="text-sm font-medium text-foreground">
          {label}
          {required && <span className="ml-1 text-destructive">*</span>}
        </Label>
        {acceptedFormat && (
          <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            {acceptedFormat}
          </span>
        )}
      </div>

      {helperText && (
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent" />
          <span>{helperText}</span>
        </p>
      )}

      <Field
        data-testid={dataTestId}
        aria-invalid={!!validationError}
        className={cn(validationError && "border-destructive focus-visible:ring-destructive")}
        {...inputProps}
      />

      {exampleText && (
        <p className="flex items-start gap-1.5 rounded-md bg-accent-subtle px-2.5 py-1.5 text-xs text-muted-foreground">
          <Lightbulb className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent" />
          <span><span className="font-medium text-foreground">Example: </span>{exampleText}</span>
        </p>
      )}

      {validationError && (
        <p className="flex items-center gap-1.5 text-xs font-medium text-destructive" data-testid={dataTestId ? `${dataTestId}-error` : undefined}>
          <AlertCircle className="h-3.5 w-3.5" />
          {validationError}
        </p>
      )}

      {whyItMatters && (
        <div>
          <button
            type="button"
            onClick={() => setShowWhy((s) => !s)}
            data-testid={dataTestId ? `${dataTestId}-why` : undefined}
            className="flex items-center gap-1 text-xs font-medium text-accent transition-colors hover:text-primary"
          >
            <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", showWhy && "rotate-180")} />
            Why this matters
          </button>
          {showWhy && (
            <p className="mt-1.5 animate-fade-up rounded-md border border-border bg-muted/50 px-3 py-2 text-xs leading-relaxed text-muted-foreground">
              {whyItMatters}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default GuidedInput;
